import React, { useCallback, useMemo } from 'react';
import { Map as ImmutableMap } from 'immutable';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TableFooter from '@mui/material/TableFooter';

import { transactionsTypes } from 'companion-app-components/flux/transactions';

import { formatAmount } from 'components/QCurrency/core';
import { isOldEdge } from 'utils/utils';
import StdDialog from 'components/Dialogs/StdDialog';
import { filterCalendarTxnBasedOnPreferences, filterDashboardCalendarTxnBasedOnPref } from 'data/transactions/selectors';
import TableHeader from './TableHeader';
import TableTxnRow from './TableTxnRow';
import { calendarTxnModalStyles } from '../../styles';

const isIe = require('is-iexplorer') || isOldEdge();  // eslint-disable-line

const useStyles = makeStyles()(calendarTxnModalStyles as Record<string, any>);

const tableColumns = Object.freeze({
  reminderCol: {
    label: '',
  },
  payeeCol: {
    label: 'Payee',
  },
  accountCol: {
    label: 'Account',
  },
  categoryCol: {
    label: 'Category',
  },
  amountCol: {
    label: 'Amount',
  },
  optionsCol: {
    label: '',
  },
});

interface CalTxnsModalProps {
  onClose: () => void;
  date: string;
  txns: ImmutableMap<string, transactionsTypes.CashFlowTransaction> | undefined;
  onGoToRegister: (txn: transactionsTypes.CashFlowTransaction) => void;
  onGoToBills: (stModelId: string) => void;
  useDashboardPrefFilter?: boolean | undefined ;
}

const CalTxnsModal: React.FC<CalTxnsModalProps> = (props) => {
  const { classes } = useStyles();
  const { onClose, date, txns, onGoToRegister, onGoToBills, useDashboardPrefFilter } = props;

  const filterCalendarTxnList = useDashboardPrefFilter ? filterDashboardCalendarTxnBasedOnPref : filterCalendarTxnBasedOnPreferences;
  const filteredTxnList = useSelector((state) => filterCalendarTxnList(state, { txnList: txns }));
  const modalTitle = useMemo(() => <div className={classes.modalTitle}>{date}</div>, [date, classes]);
  const txnsTotal = useMemo(() => filteredTxnList &&
    filteredTxnList.reduce((acc: number, txn: transactionsTypes.CashFlowTransaction) => acc + parseFloat(txn.amount ?? 0), 0), [filteredTxnList]);

  const onCloseHandler = useCallback((event: Record<string, any>) => {
    event.stopPropagation();
    if (onClose) onClose();
  }, [onClose]);

  const onHandleModalClick = useCallback((event) => event.stopPropagation(), []);

  const tableContent = useMemo(() => (
    filteredTxnList?.size ? (
      <TableContainer className={classes.tableContainer}>
        <Table stickyHeader padding="none" size="small">
          <TableHeader columns={tableColumns} classes={classes} />
          <TableBody>
            {filteredTxnList.map((txn: transactionsTypes.CashFlowTransaction) =>
              <TableTxnRow
                key={`row-${txn.id}`}
                columns={tableColumns}
                classes={classes}
                txn={txn}
                onGoToRegister={onGoToRegister}
                onGoToBills={onGoToBills}
              />)}
          </TableBody>
        </Table>
        <Table padding="none" size="small" className={classes.footerTable}>
          <TableFooter>
            <TableRow>
              <TableCell className={classes.reminderCol} />
              <TableCell className={classes.totalLabelCell}>
                <div className={classes.cellContainer}>Total</div>
              </TableCell>
              <TableCell className={classes.totalAmountCell}>
                <div className={classes.cellContainer}>{formatAmount(txnsTotal)}</div>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    ) : <div className={classes.noTransactions}>No Transactions</div>
  ), [classes, filteredTxnList, txnsTotal, onGoToBills, onGoToRegister]);

  return (
    <StdDialog
      open
      classes={isIe ? { paper: classes.dialogPaper } : {}}
      onClose={onCloseHandler}
      title={modalTitle}
      maxWidth="xlg"
      onClick={onHandleModalClick}
    >
      <DialogContent sx={{ padding: '0 !important' }}>
        {tableContent}
      </DialogContent>
      <div className={classes.buttonArea}>
        <Button
          color="primary"
          onClick={onCloseHandler}
          id="txn-modal-done"
        >
          Done
        </Button>
      </div>

    </StdDialog>
  );
};


export default React.memo(CalTxnsModal);
